import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from '../../services/api';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const Title = styled.h2`
  color: #fff;
  font-size: 24px;
  margin: 0;
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background: #4caf50;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #45a049;
    transform: translateY(-2px);
  }
`;

const Table = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 1fr 0.8fr;
  gap: 20px;
  padding: 16px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  font-weight: 600;
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 0.8fr;

    & > :nth-child(4) {
      display: none;
    }
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 1fr 0.8fr;
  gap: 20px;
  padding: 16px;
  border-bottom: 1px solid #333;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    background: #1a1a1a;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 0.8fr;

    & > :nth-child(4) {
      display: none;
    }
  }
`;

const StaffName = styled.p`
  color: #fff;
  font-weight: 600;
  margin: 0;
  font-size: 14px;
`;

const Email = styled.p`
  color: #999;
  margin: 0;
  font-size: 13px;
`;

const Phone = styled.p`
  color: #fff;
  margin: 0;
  font-size: 14px;
`;

const Role = styled.p`
  color: #ff6b35;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 6px 10px;
  background: none;
  border: 1px solid #333;
  border-radius: 4px;
  color: #ff6b35;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: #ff6b35;
    color: #fff;
    border-color: #ff6b35;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #f44336;

  &:hover {
    background: #f44336;
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #999;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
`;

const ModalTitle = styled.h3`
  color: #fff;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  display: block;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 10px;
  background: #4caf50;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #45a049;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 10px;
  background: #666;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #555;
  }
`;

const AdminStaff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'staff'
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await api.adminGetStaff();
      if (response.success && response.data) {
        setStaff(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching staff:', err);
      setLoading(false);
    }
  };

  const handleOpenModal = (staffMember = null) => {
    if (staffMember) {
      setEditingStaff(staffMember);
      setFormData({
        name: staffMember.name,
        email: staffMember.email,
        phone: staffMember.phone,
        role: staffMember.role
      });
    } else {
      setEditingStaff(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: 'staff'
      });
    }
    setShowModal(true);
  };

  const handleSaveStaff = async () => {
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      if (editingStaff) {
        const response = await api.adminUpdateStaff(editingStaff.id, formData);
        if (response.success) {
          setStaff(staff.map(s =>
            s.id === editingStaff.id ? { ...s, ...formData } : s
          ));
        }
      } else {
        const response = await api.adminCreateStaff(formData);
        if (response.success && response.data) {
          setStaff([...staff, response.data]);
        }
      }
      setShowModal(false);
    } catch (err) {
      console.error('Error saving staff:', err);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        const response = await api.adminDeleteStaff(staffId);
        if (response.success) {
          setStaff(staff.filter(s => s.id !== staffId));
        }
      } catch (err) {
        console.error('Error deleting staff:', err);
      }
    }
  };

  if (loading) {
    return <Container>Loading staff...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>Staff Management</Title>
        <AddButton onClick={() => handleOpenModal()}>
          <FiPlus /> Add Staff
        </AddButton>
      </Header>

      {staff.length > 0 ? (
        <Table>
          <TableHeader>
            <div>Name</div>
            <div>Email</div>
            <div>Phone</div>
            <div>Role</div>
            <div>Actions</div>
          </TableHeader>
          {staff.map((staffMember, index) => (
            <TableRow
              key={staffMember.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <StaffName>{staffMember.name}</StaffName>
              <Email>{staffMember.email}</Email>
              <Phone>{staffMember.phone}</Phone>
              <Role>{staffMember.role}</Role>
              <Actions>
                <ActionButton onClick={() => handleOpenModal(staffMember)}>
                  <FiEdit2 /> Edit
                </ActionButton>
                <DeleteButton onClick={() => handleDeleteStaff(staffMember.id)}>
                  <FiTrash2 /> Delete
                </DeleteButton>
              </Actions>
            </TableRow>
          ))}
        </Table>
      ) : (
        <EmptyState>No staff members found. Add your first staff member!</EmptyState>
      )}

      {showModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowModal(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
            </ModalTitle>
            <FormGroup>
              <Label>Name</Label>
              <Input
                type="text"
                placeholder="Enter staff name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Phone</Label>
              <Input
                type="tel"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Role</Label>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="staff">Staff</option>
                <option value="manager">Manager</option>
                <option value="delivery">Delivery</option>
              </Select>
            </FormGroup>
            <ButtonGroup>
              <SaveButton onClick={handleSaveStaff}>Save</SaveButton>
              <CancelButton onClick={() => setShowModal(false)}>Cancel</CancelButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminStaff;
